import Toasted from 'vue-toasted';
import { EventBus } from '@v4/common/utils/event-bus';
import clickToCallModalHandler from '@v4/common/components/mixins/click-to-call-modal-handler';

(async () => {
    const el = document.getElementById('multi-section');

    if (!el) {
        return;
    }

    const fetchMixinById = (id = '') =>
        import(/* webpackChunkName: "multi-section-[request]" */ `@v4/multi-section/mixins/${id}/`)
            .then((mixin) => mixin.default)
            .catch(() => Promise.resolve(() => ({})));

    const { getJsonDataFromScript } = await import(
        /* webpackChunkName: "getJsonDataFromScript" */ '@v4/common/utils/json'
    );
    const fetchedData = getJsonDataFromScript('#multi-section script[type="application/json"]');

    const isVipPage = fetchedData.id === 'version';

    fetchMixinById(fetchedData.id).then((mixin) => {
        if (document.getElementById('header')) {
            import(/* webpackChunkName: "header" */ '@v4/header').then(({ default: Header }) => Header());
        }

        import('vue')
            .then((VueModule) => VueModule.default)
            .then(async (Vue) => {
                import('vue-js-modal')
                    .then((VueModalModule) => VueModalModule.default)
                    .then((VueModal) => {
                        Vue.use(VueModal, { dynamic: true, injectModalsContainer: true });
                        Vue.use(Toasted);

                        import('@v4/locale/async').then(async ({ i18n, loadDefaultLanguage }) => {
                            await loadDefaultLanguage();
                            import('@v4/common/utils/lazy-load');

                            const methods = (await import('@v4/multi-section/methods')).default;
                            const computed = (await import('@v4/multi-section/computed')).default;

                            const gaEventHandler = (await import(
                                /* webpackChunkName: "gaEventHandler", webpackPreload: true */ '@v4/common/components/mixins/google-analytics'
                            )).default;
                            const dataLayerHandler = (await import(
                                /* webpackChunkName: "dataLayerHandler", webpackPreload: true */ '@v4/common/components/mixins/data-layer-handler'
                            )).default;
                            const { modalServiceCompareVehicle } = await import(
                                /* webpackChunkName: "modalServiceCompareVehicle", webpackPreload: true */ '@v4/common/components/mixins/modal-service'
                            );
                            const { dkModalAware } = await import(
                                /* webpackChunkName: "dkModalAware", webpackPreload: true */ '@v4/common/components/mixins/modal'
                            );
                            const { notifier } = await import(
                                /* webpackChunkName: "notifier", webpackPreload: true */ '@v4/common/components/mixins/mixins'
                            );

                            const mediaGallery = (fetchedData.media && fetchedData.media.gallery) || [];
                            const components = {
                                AutoHideNav: () => ({
                                    component: import(
                                        /* webpackChunkName: "AutoHideNav-vue", webpackPreload: true */ '@v4/common/components/atoms/AutoHideNav.vue'
                                    ),
                                }),
                                Incentive: () => ({
                                    component: import(
                                        /* webpackChunkName: "Incentive-vue", webpackPreload: true */ '@v4/common/components/molecules/Incentive.vue'
                                    ),
                                }),
                                DkScore: () => ({
                                    component: import(
                                        /* webpackChunkName: "DkScore-vue", webpackPreload: true */ '@v4/common/components/molecules/DkScore.vue'
                                    ),
                                }),
                                MediaCounter: () => ({
                                    component: import(
                                        /* webpackChunkName: "MediaCounter-vue", webpackPreload: true */ '@v4/common/components/molecules/DkMediaCounterDST.vue'
                                    ),
                                }),
                                LeadFormWrapper: () => ({
                                    component: import(
                                        /* webpackChunkName: "LeadFormWrapper-vue", webpackPreload: true */ '@v4/common/components/organisms/LeadFormWrapper/LeadFormWrapper.vue'
                                    ),
                                }),
                                DkSlide: () => ({
                                    component: import(
                                        /* webpackChunkName: "DkSlide-vue", webpackPreload: true */ '@v4/common/components/atoms/DkSlide/DkSlide.vue'
                                    ),
                                }),
                                DkSlider: () => ({
                                    component: import(
                                        /* webpackChunkName: "DkSlider-vue", webpackPreload: true */ '@v4/common/components/molecules/DkSliderDST.vue'
                                    ),
                                }),
                                DkButton: () => ({
                                    component: import(
                                        /* webpackChunkName: "DkButton-vue", webpackPreload: true */ '@v4/common/components/atoms/DkButton.vue'
                                    ),
                                }),
                                DkIcon: () => ({
                                    component: import(
                                        /* webpackChunkName: "DkIcon-vue", webpackPreload: true */ '@v4/common/components/atoms/DkIcon.vue'
                                    ),
                                }),
                            };

                            if (fetchedData.hasSuggestions && !isVipPage) {
                                components.LazySuggestedCars = () => ({
                                    component: import(
                                        /* webpackChunkName: "LazySuggestedCars-vue", webpackPreload: true */ '@v4/multi-section/components/molecules/LazySuggestedCars'
                                    ),
                                });
                            }

                            if (fetchedData.hasComparisons && !isVipPage) {
                                components.LazyComparedCars = () => ({
                                    component: import(
                                        /* webpackChunkName: "LazyComparedCars-vue", webpackPreload: true */ '@v4/multi-section/components/molecules/LazyComparedCars'
                                    ),
                                });
                            }

                            if (mediaGallery.length) {
                                components.DkMediaSwipeViewer = () => ({
                                    component: import(
                                        /* webpackChunkName: "DkMediaSwipeViewer-vue", webpackPreload: true */ '@v4/common/components/organisms/DkMediaSwipeViewer/DkMediaSwipeViewer.vue'
                                    ),
                                });
                            }

                            if (fetchedData.hasPromotions && !isVipPage) {
                                components.LazyPromotions = () => ({
                                    component: import(
                                        /* webpackChunkName: "LazyPromotions-vue", webpackPreload: true */ '@v4/multi-section/components/molecules/LazyPromotions'
                                    ),
                                });
                            }

                            new Vue({
                                el,
                                i18n,
                                components,
                                mixins: [
                                    mixin({ fetchedData }),
                                    gaEventHandler,
                                    dataLayerHandler,
                                    dkModalAware,
                                    modalServiceCompareVehicle,
                                    notifier,
                                    clickToCallModalHandler,
                                ],
                                data() {
                                    return {
                                        fetchedData,
                                        isVipPage,
                                        form: {
                                            busy: false,
                                            title: '',
                                            subtitle: '',
                                        },
                                        sidebar: {
                                            title: '',
                                            subtitle: '',
                                        },
                                        heroSwiperOptions: {
                                            slidesPerView: 1,
                                            pagination: false,
                                            navigation: false,
                                            loop: true,
                                            preloadImages: false,
                                            lazy: {
                                                loadPrevNext: false,
                                                loadOnTransitionStart: true,
                                            },
                                        },
                                        mediaSwipeProps: {
                                            swipeOptions: { history: false },
                                            items: mediaGallery,
                                        },
                                        mediaSwipeEvents: {
                                            init: this.onMediaSwipeInit,
                                            close: this.onMediaSwipeClose,
                                        },
                                        hasPromotions: true,
                                        hasSuggestions: fetchedData.hasSuggestions || true,
                                        hasComparisons: fetchedData.hasComparisons || true,
                                    };
                                },
                                computed,
                                created() {
                                    const subtitle = this.$t('multi-section-submodel.sidebar.subtitle');

                                    this.setSidebarMetaData({
                                        title: this.$t('multi-section-submodel.sidebar.title'),
                                        subtitle,
                                    });
                                    EventBus.$on('VersionPage:setOptionalsField', (optionals) => {
                                        const className = 'show-sticky-configuration-stepper';
                                        if (optionals.length) {
                                            this.$el.classList.add(className);
                                        } else {
                                            this.$el.classList.remove(className);
                                        }
                                    });
                                },
                                mounted() {
                                    if (this.fetchedData.showClickToCallPopupTimed) {
                                        this.showClickToCallPopup();
                                    }
                                },
                                methods,
                            });
                        });
                    });
            });
    });
})();
